"use client";

import React, { useState } from "react";
import { MemberProfileType } from "../../../../../types/profiles/memberProfileTypes";
import { MemberServiceTypeDto } from "../../../../../types/profiles/memberServicesTypes";
import { CustomUserType } from "../../../../../types/profiles/authTypes";
import { LitCovenantFellowshipType } from "../../../../../types/network/fellowshipTypes";
import { MemberSpiritualGiftsReport } from "../../../../../types/profiles/giftTypes";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { VisitorFriend } from "@/types/profiles/visitorProfileTypes";

import ProfileAvatars from "./profile-avatars/ProfileAvatars";
import PersonalDetails from "./personal-details/PersonalDetails";
import SpiritualDetails from "./spiritual-details/SpiritualDetails";
import SpiritualServices from "./spiritual-services/SpiritualServices";
import "./ProfileCarousel.css";

interface ProfileCarouselProps {
  profileImageUrl: string; 
  placeholderSrc: string;
  name: string;
  labelColor: string;
  customUser: CustomUserType;
  spiritualRebirthDay: string | null;
  biography: string | null;
  vision: string | null;
  showFellowship: boolean;
  litcovenant: LitCovenantFellowshipType[];
  ownerUserId: number;
  serviceTypes: MemberServiceTypeDto[];

  organizationMemberships: MemberProfileType["organization_memberships"];
  academicRecord: MemberProfileType["academic_record"];

  spiritualGifts?: MemberSpiritualGiftsReport | null;
  showGiftsInProfile?: boolean;

  denominationBranchLabel?: string;
  denominationFamilyLabel?: string | null;

  // Friends coming from serializer (randomized)
  friends?: VisitorFriend[];

  isVerifiedIdentity?: boolean;
  isTownlitVerified?: boolean;
}

const ProfileCarousel: React.FC<ProfileCarouselProps> = ({
  profileImageUrl,
  placeholderSrc,

  name,
  labelColor,

  customUser,
  spiritualRebirthDay,
  biography,
  vision,
  showFellowship,
  litcovenant,
  ownerUserId,

  serviceTypes,
  organizationMemberships,
  academicRecord,

  spiritualGifts,
  showGiftsInProfile,

  denominationBranchLabel,
  denominationFamilyLabel,
  friends,

  isVerifiedIdentity,
  isTownlitVerified,
  
}) => {
  // Safe default to avoid undefined downstream
  const friendsData: VisitorFriend[] = friends ?? [];

  // Define carousel slides (kept lightweight; each slide is a self-contained component)
  const slides = [
    {
      component: (
        <ProfileAvatars
          profileImageUrl={profileImageUrl}
          placeholderSrc={placeholderSrc}
          customUser={customUser}
          labelColor={labelColor}
          isVerifiedIdentity={isVerifiedIdentity}
          isTownlitVerified={isTownlitVerified}
          friends={friendsData} // pass randomized friends from serializer
        />
      ),
      label: "Profile Pictures",
    },
    {
      component: (
        <PersonalDetails
          customUser={customUser}
          spiritualRebirthDay={spiritualRebirthDay}
          // ownerUserId={ownerUserId} // enable if needed later
        />
      ),
      label: "Personal Details",
    },
    {
      component: (
        <SpiritualDetails
          biography={biography}
          vision={vision}
          showFellowship={showFellowship}
          litcovenant={litcovenant}
          ownerUserId={customUser.id}
        />
      ),
      label: "Spiritual Details",
    },
    {
      component: (
        <SpiritualServices
          serviceTypes={serviceTypes}
          memberServices={serviceTypes}
          organizationMemberships={organizationMemberships}
          academicRecord={academicRecord}
          customUser={customUser}
          spiritualGifts={spiritualGifts}
          showGiftsInProfile={showGiftsInProfile}
          denominationBranchLabel={denominationBranchLabel}
          denominationFamilyLabel={denominationFamilyLabel}
        />
      ),
      label: "Spiritual Services",
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  // Carousel navigation (wrap-around)
  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  return (
    <div className="profile-carousel-container">
      <div className="profile-carousel-slides">
        <div
          className="profile-carousel-inner"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide, index) => (
            <div key={index} className="carousel-slide">
              {slide.component}
            </div>
          ))}
        </div> 

        {/* Simple prev/next controls */}
        <button className="carousel-key-controls carousel-prev" onClick={handlePrev} aria-label="Previous slide">
          <ChevronLeft strokeWidth={2} />
        </button>
        <button className="carousel-key-controls carousel-next" onClick={handleNext} aria-label="Next slide">
          <ChevronRight strokeWidth={2} />
        </button>
      </div>
    </div> 
  );
};

export default ProfileCarousel;
